import React, { useEffect, useRef } from 'react';
import { Drawer, Button, ButtonProps } from 'antd';
import type { DrawerProps } from 'antd/lib/drawer';
import Iconfont from '@base/lib/Iconfont';
import cn from 'classnames';
import styles from './index.module.less';
import uaUtils from 'ava-util/dist/user-agent';
import { useRouter } from 'next/router'
import delay from 'base/utils/delay';
import { generateId } from 'base/utils/randomGenerator';
import { observer } from 'base/store';
import { addClass, removeClass } from 'ava-util/dist/dom-class';

type ICloseIconType = 'close' | 'arrow';//

export type IPureDrawerProps = DrawerProps & {
    id?: string;
    isHash?: boolean;   // 打开窗口是 push当前页面 url history加一
    isActive?: boolean; // 多个抽屉弹框时， 当前drawer是否最上层、活跃
    closeIconType?: ICloseIconType,
    closePlacement?: 'left' | 'right';
    headerBorder?: boolean;
    footerBorder?: boolean;
    onClose: () => void;
    toTopCounter?: number;
}

export const delaySetDrawerActive = async (fn: () => void, isDelay?: boolean) => {
    if (isDelay) {
        // url hash更改中 需要延迟执行（问题：会立即执行hashchange 关闭当前（：前一个）弹窗）
        await delay(50)
    }
    fn()
}

export const onDrawerHistoryBack = (delta?: number) => {
    // 点击关闭 触发popstate监听方法
    window.history.go(delta || -1)
}


type IFooterSingleBtnProps = ButtonProps & {
    text: string;
};

export const lockWindowScroll = () => {
    const htmlEl = document.getElementsByTagName('html')[0];
    const bodyEl = document.body;
    htmlEl.style.height = '100%';
    htmlEl.style.overflow = 'hidden';
    bodyEl.style.height = '100%';
    bodyEl.style.overflow = 'hidden';
    addClass(bodyEl, 'js-keep-style', false);

};

export const unlockWindowScroll = () => {
    const openDrawer = document.querySelector('.ant-drawer-open');
    if (!openDrawer) {
        const htmlEl = document.getElementsByTagName('html')[0];
        const bodyEl = document.body;
        htmlEl.style.height = '';
        htmlEl.style.overflow = '';
        bodyEl.removeAttribute('style')
        removeClass(bodyEl, 'js-keep-style', false);
    }
};

export const unlockWindowScrollDelay= async (timer = 300) => {
    await delay(timer)
    unlockWindowScroll()
}

export const FooterSingleBtn: React.FC<IFooterSingleBtnProps> = props => {
    const { className, text, ...rest } = props;
    return (
        <Button
            className={className}
            type="primary"
            block
            {...rest}
        >
            {text}
        </Button>
    )
};

export const PureDrawer: React.FC<IPureDrawerProps> = observer(props => {
    const isOpenedRef: any = useRef(false);
    // 系统返回标识
    const isSystemBackRef: any = useRef(false);
    const drawerHashRef = useRef<string>(`drawer-${generateId()}`);
    const drawerHash = drawerHashRef.current;
    const contentRef = useRef<HTMLDivElement>(null);

    const router = useRouter()

    const {
        className,
        isHash = process.env.NEXT_PUBLIC_TERMINAL === 'm',
        isActive = true,
        headerBorder = false,
        footerBorder = false,
        //DrawerProps
        placement = 'right',
        open,
        onClose,
        children,
        toTopCounter,
        height: propsHeight,
        ...rest1
    } = props

    let height = propsHeight || (['top', 'bottom'].includes(placement) ? 'auto' : '100%');

    let { closeIcon, closePlacement, extra, ...rest2 } = rest1;
    // 左右滑动 close 默认在左侧，上下滑动 close 默认在右侧
    closePlacement = closePlacement || (['right', 'left'].includes(placement) ? 'left' : 'right');
    // 默认显示关闭按钮图标，左侧滑出默认左箭头，右侧滑出默认x
    closeIcon = closeIcon || (
        <Iconfont
            className={closePlacement === 'left' ? 'icon-arrow-left' : 'icon-x'}
            tabIndex={0}
            aria-label={'close Drawer'}
        />
    );
    // extra 设置抽屉右上角的操作区域，所以关闭按钮强制显示在左侧
    if (extra) {
        closePlacement = 'left';
    }

    const isIOS = uaUtils.isIOS();

    // 监听hash变化
    const onHashChange = (e: HashChangeEvent) => {
        // console.log(`物理按键回退 -- 触发hashChange -->> open:${open};isActive:${isActive};isIOS:${isIOS};`)
        if (open && isActive && isOpenedRef.current && !window.location.hash.replace('#', '').split('|').includes(drawerHash)) {
            // console.log(`物理按键回退 -- 执行 ${drawerHash} -->> active:${isActive} -->> ios:${isIOS} -->> close！`)
            isOpenedRef.current = false
            isSystemBackRef.current = true
            onClose?.()
            isSystemBackRef.current = false
            unlockWindowScroll();
        }
    }

    const openDrawerHashHandle = async () => {
        if (isHash) {
            await delay(100)            // 延迟（更改url hash变化使弹窗无法打开）
            // window.location.hash=drawerHash;     // 直接修改hash (问题: 详情页面切换变体replace url后,关闭弹窗会重新刷新页面...)

            const anchor = window.location.hash
            const newUrl = anchor ?
                window.location.href.replace(anchor, `${anchor}|${drawerHash}`) :
                `${window.location.href}#${drawerHash}`

            // 打开弹窗 添加一条当前 url记录
            router.push(newUrl)
            await delay(100)
            lockWindowScroll();
        }
    }

    const scrollToTop = (duation: number) => {
        const contentEl = contentRef.current;
        if (!contentEl) {
            return;
        }
        const scrollContainer = contentEl.closest('.ant-drawer-body');
        if (scrollContainer) {
            setTimeout(() => {
                scrollContainer.scrollTop = 0;
            }, duation)
        }
    };

    useEffect(() => {
        if (open) {
            scrollToTop(200);
            if (isHash && isActive) {
                if (!isOpenedRef?.current) {
                    openDrawerHashHandle();
                }
                window.addEventListener('hashchange', onHashChange, false);
            }
            isOpenedRef.current = true
            // lockWindowScroll();

        } else {
            // 关闭
            if (isHash && isActive && isOpenedRef?.current) {
                onDrawerHistoryBack()
                window.removeEventListener('hashchange', onHashChange)
                unlockWindowScroll();
            }
            isOpenedRef.current = false
        }

        return () => {
            window.removeEventListener('hashchange', onHashChange)
        }
    }, [open, isActive])

    useEffect(() => {
        scrollToTop(0);
    }, [toTopCounter]);

    return (
        <Drawer
            className={cn(
                className,
                styles.drawer,
                { [styles['header-close-right']]: closePlacement === 'right' },
                // 关闭过度动画 ios系统左滑回退页面关闭抽屉 会有闪烁
                { [styles['disable-transition']]: isIOS && isSystemBackRef.current }
            )}
            placement={placement}
            height={height}
            open={open}
            onClose={onClose}
            closeIcon={closeIcon}
            extra={extra}
            push={false}
            headerStyle={{
                borderBottom: !headerBorder ? 'none' : void 0
            }}
            footerStyle={{
                borderTop: !footerBorder ? 'none' : void 0
            }}
            {...rest2}
        >
            <div ref={contentRef} className="g-drawer-body-content">
                {children}
            </div>
        </Drawer>
    )
})

export default PureDrawer